"use client";

import { Button } from "@ui/components/core/Button";
import ChevronLeft24 from "@ui/icons/24x24/chevron-left-24.svg";
import ChevronRight24 from "@ui/icons/24x24/chevron-right-24.svg";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useMemo, useState } from "react";
interface CircleItem {
  description?: null | string;
  title?: null | string;
}

interface CircleExpanderProps {
  items: CircleItem[];
}

export default function CircleExpander({ items }: CircleExpanderProps) {
  const [activeIndex, setActiveIndex] = useState(items.length - 1);
  const itemsInOrder = useMemo(() => [...items].reverse(), [items]);

  const handlePrevious = () => {
    setActiveIndex(prev => (prev < itemsInOrder.length - 1 ? prev + 1 : 0));
  };

  const handleNext = () => {
    setActiveIndex(prev => (prev > 0 ? prev - 1 : itemsInOrder.length - 1));
  };

  const getCircleProps = (index: number) => {
    return {
      width: `${100 - index * 10}%`,
      topOffset: `${index * 6}px`,
    };
  };

  return (
    <div className='flex flex-col items-center'>
      {/* Circular Diagram */}
      <div className='relative mb-60 flex w-full items-center justify-center'>
        <div className='relative aspect-1x1 w-full max-w-[500px]'>
          {itemsInOrder.map((item, index) => {
            const size = getCircleProps(index);
            const isActive = index === activeIndex;

            return (
              <button
                className={`group absolute left-1/2 flex aspect-1x1 -translate-x-1/2 flex-col rounded-full border ${isActive ? "border-solid" : "border-dashed"}`}
                key={index}
                onClick={() => setActiveIndex(index)}
                style={{
                  width: `${size.width}`,
                  borderColor: isActive ? "rgba(123, 123, 123, 0.60)" : "rgba(123, 123, 123, 0.60)",
                  top: `${size.topOffset}`,
                }}
              >
                {isActive && (
                  <m.div
                    animate={{ opacity: 1 }}
                    className='absolute inset-0 z-2 rounded-full bg-default'
                    initial={{ opacity: 0.5 }}
                  />
                )}
                {/* Label */}
                <div className='z-3 mb-6 mt-auto flex flex-col items-center'>
                  <span
                    className={cn(
                      "effect-color f-paragraph-03 text-light group-hover:text-default",
                      isActive && "text-default",
                    )}
                  >
                    {item.title}
                  </span>
                </div>
                <div
                  className={cn(
                    "absolute -bottom-6 left-1/2 z-1 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full bg-neutral-dark",
                    activeIndex < index && "bg-default",
                  )}
                />
                <div className='absolute -bottom-3 left-1/2 z-3 h-6 w-6 -translate-x-1/2 rounded-full bg-darkest' />
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Section */}
      <div className='relative mb-24 min-h-140 max-w-2xl text-center'>
        {itemsInOrder.map((item, index) => (
          <div
            className={cn(
              "space-y-8 transition-opacity duration-300",
              index === activeIndex ? "opacity-100" : "pointer-events-none absolute inset-0 scale-0 opacity-0",
            )}
            key={index}
          >
            <h2 className='f-heading-04'>{item?.title}</h2>
            <p className='text-light'>{item?.description}</p>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div className='flex space-x-20'>
        <Button onClick={handlePrevious} size='pill' variant='elevated'>
          <ChevronLeft24 />
        </Button>
        <Button onClick={handleNext} size='pill' variant='elevated'>
          <ChevronRight24 />
        </Button>
      </div>
    </div>
  );
}
